/**
 * Formato canonico degli indirizzi:
 *   "{via} {numero}, {cap}, {città} ({Provincia})"
 * es. "Via Cav. Pierino Migliorati 28/B, 25020, Pavone Mella (BS)".
 *
 * Il parser accetta anche il formato legacy "{via} {numero}, {città}, {cap} ({Provincia})".
 */

export interface AddressParts {
  /** Via completa di eventuale tipo (es. "Via Roma"), senza numero civico. */
  via: string;
  numero: string;
  citta: string;
  cap: string;
  provincia: string;
}

/** Numero civico: cifre + suffisso opzionale (3a, 3/5, 28/B, 12 bis, 4 ter, 4 quater). */
const NUMERO_CIVICO_PATTERN =
  '\\d+[a-zA-Z]?(?:\\s*/\\s*[0-9A-Za-z]+)?(?:\\s+(?:bis|ter|quater))?';

const STREET_WITH_NUMBER = new RegExp(`^(.+?)\\s+(${NUMERO_CIVICO_PATTERN})$`, 'i');
const PROVINCE_AT_END = /\s*\(([^()]+)\)\s*$/;
const CAP_ONLY = /^\d{4,5}$/;

export function parseAddress(address: string | null | undefined): AddressParts {
  const result: AddressParts = { via: '', numero: '', citta: '', cap: '', provincia: '' };
  let rest = (address ?? '').trim();
  if (!rest) return result;

  const provinceMatch = rest.match(PROVINCE_AT_END);
  if (provinceMatch) {
    result.provincia = provinceMatch[1].trim();
    rest = rest.slice(0, provinceMatch.index).trim();
  }

  const [street = '', ...others] = rest.split(',').map(part => part.trim());

  const streetMatch = street.match(STREET_WITH_NUMBER);
  if (streetMatch) {
    result.via = streetMatch[1].trim();
    result.numero = streetMatch[2].trim();
  } else {
    result.via = street;
  }

  // Dopo la via: CAP e città in qualsiasi ordine (formato nuovo o legacy).
  const cityParts: string[] = [];
  for (const part of others) {
    if (!part) continue;
    if (!result.cap && CAP_ONLY.test(part)) {
      result.cap = part;
    } else {
      cityParts.push(part);
    }
  }
  result.citta = cityParts.join(', ');

  return result;
}

export function formatAddress(parts: AddressParts): string {
  const segments: string[] = [];

  const street = [parts.via, parts.numero]
    .map(p => p.trim())
    .filter(Boolean)
    .join(' ');
  if (street) segments.push(street);

  const cap = parts.cap.trim();
  if (cap) segments.push(cap);

  const citta = parts.citta.trim();
  const provincia = parts.provincia.trim();
  if (citta && provincia) {
    segments.push(`${citta} (${provincia})`);
  } else if (citta) {
    segments.push(citta);
  } else if (provincia) {
    segments.push(`(${provincia})`);
  }

  return segments.join(', ');
}
